import { FC, Fragment } from 'react';
import { Box, Card, Grid, Typography } from '@mui/material';
//components
import GummyForm from './GummyForm';
import TabletForm from './TabletForm';
import PowderForm from './PowderForm';
import LiquidForm from './LiquidForm';
import CapsuleForm from './CapsuleForm';
import FormulaAgeGroupCard from './FormulaAgeGroup';
import SelectController from 'controller/Select';
import InputController from 'controller/InputController';
import { InfoTooltip } from 'components/common/InfoTooltip';
import ClearComponent from 'components/common/ClearComponent';
import RadioGroupController from 'controller/RadioGroupController';
import ProductTypeSelector from 'components/common/radioGroups/productType';
import ProductSubTypeSelector from 'components/common/radioGroups/productSubType';
import ProductFormulationTypeSelector from 'components/common/selectors/productFormulationType';
//constant, styles, context, interfaces
import {
  FORM_TEXT,
  BOTTLE_CAP_TEXT,
  BOTTLE_TYPE_TEXT,
  BOTTLE_TYPE_MAPPED,
  formulationValues,
  BOTTLE_CAP_MAPPED,
  PRODUCT_TYPE_TEXT,
  selectTypeInitialValues,
  PRODUCT_FORMULATION_TEXT,
  dosageFormulaFormInitialValues,
  productFormulationTypeInitialValues,
  CO_MFG_COST_TYPE_TEXT,
  CO_MFG_COST_TYPE_MAPPED,
  CO_MFG_COST_PERCENTAGE_TEXT,
  OPTIONAL_TEXT,
  MISCELLANEOUS_COST_USD_TEXT,
  MISCELLANEOUS_COST_INFO,
  NOTES_TEXT,
} from 'constants/index';
import { ProductTypes } from 'generated/graphql';
import { forInputLabelBg } from 'styles/commonComponentStyle';
import { ProductTypeSelect, SelectType, Step1Props } from 'interfaces';
import { flexCenterJustifyBetween } from 'theme/styleConstant';

const Step1: FC<Step1Props> = ({
  type,
  reset,
  xl = 7,
  setValue,
  resetBlends,
  factBoxType,
  productTypeId,
  productSubType,
  isFactBox = false,
  productTypeDispatch,
  productSubTypeDispatch,
  resetFormulaIngredients,
  productFormulationTypeDispatch,
}) => {
  const formView = () => {
    switch (type) {
      case ProductTypes.Powder:
        return <PowderForm isFactBox={isFactBox} />;

      case ProductTypes.Tablet:
        return <TabletForm />;

      case ProductTypes.Capsule:
        return <CapsuleForm />;

      case ProductTypes.Gummies:
        return <GummyForm isFactBox={isFactBox} />;

      case ProductTypes.Liquids:
        return <LiquidForm isFactBox={isFactBox} />;

      case ProductTypes.Bars:
        return <Fragment />;

      case ProductTypes.Beverages:
        return <Fragment />;

      default:
        return <Fragment />;
    }
  };

  const handleOnChange = (item: ProductTypeSelect) => {
    const { value } = item || {};
    reset({ ...dosageFormulaFormInitialValues });
    setValue('productType', value, { shouldDirty: true });
    productTypeDispatch(item);
    productSubTypeDispatch(selectTypeInitialValues);
    productFormulationTypeDispatch(productFormulationTypeInitialValues);
  };

  const handleSubProductOnChange = (item: SelectType) => {
    reset({
      productType: productTypeId,
      productSubType: item?.value,
      productFormulationType: productFormulationTypeInitialValues,
      ...formulationValues,
    });
    productSubTypeDispatch(item);
    productFormulationTypeDispatch(productFormulationTypeInitialValues);
  };

  const handleProductFormulationChange = (val: ProductTypeSelect) => {
    reset({
      productType: productTypeId,
      productSubType: productSubType,
      ...formulationValues,
    });
    setValue('productFormulationType', val, { shouldDirty: true });
    productFormulationTypeDispatch(val);
    resetFormulaIngredients && resetFormulaIngredients();
    resetBlends && resetBlends();
  };

  const handleCoMfgCostTypeClear = () => {
    setValue('coMfgCostType', '');
    setValue('coMfgCostPercentage', '');
  };

  const resetCoMfgCostPercentageValueOnChange = () => {
    setValue('coMfgCostPercentage', '');
  };
  const isSubTypeProduct = type === ProductTypes.Capsule || type === ProductTypes.Gummies;
  // Bottle cap does not apply to liquid — the field is hidden and left empty.
  const isLiquid = type === ProductTypes.Liquids;

  return (
    <Grid container spacing={2}>
      {/* Product Type Card  */}
      <Grid item xs={12} xl={xl}>
        <Card sx={forInputLabelBg}>
          <ProductTypeSelector
            isRequired
            name="productType"
            isCheckbox={false}
            factBoxType={factBoxType}
            title={PRODUCT_TYPE_TEXT}
            onChange={handleOnChange}
          />
        </Card>
      </Grid>
      {/* Product Formulation Type Card  */}
      <Grid item xs={12} xl={xl}>
        <Card sx={forInputLabelBg}>
          {isSubTypeProduct ? (
            <ProductSubTypeSelector
              isRequired
              name="productSubType"
              type={type ?? ''}
              title={FORM_TEXT}
              productTypeId={productTypeId}
              onChange={handleSubProductOnChange}
            />
          ) : (
            <Fragment />
          )}

          <Box mt={2}>
            <ProductFormulationTypeSelector
              isRequired
              title={PRODUCT_FORMULATION_TEXT}
              name="productFormulationType"
              productTypeId={productTypeId}
              productSubTypeId={isSubTypeProduct ? productSubType : ''}
              disabled={!(isSubTypeProduct ? productTypeId && productSubType : productTypeId)}
              handleChange={handleProductFormulationChange}
            />
          </Box>
        </Card>
      </Grid>

      {/* Formula Age Group Card  */}
      <Grid item xs={12} xl={xl}>
        <FormulaAgeGroupCard />
      </Grid>

      {/* Common Form Fields  */}
      <Grid item xs={12} xl={xl}>
        <Card sx={forInputLabelBg}>
          <Grid container spacing={2}>
            <Grid item xs={12} xl={12}>
              <InputController
                name="miscellaneousCost"
                fieldType="number"
                title={MISCELLANEOUS_COST_USD_TEXT}
                inputAdornmentIcon={<InfoTooltip title={MISCELLANEOUS_COST_INFO} />}
              />
            </Grid>

            <Grid item xs={12} xl={12}>
              <InputController
                name="description"
                fieldType="text"
                isMultiLine
                minRows="3"
                title={NOTES_TEXT}
              />
            </Grid>

            {!isLiquid && (
              <>
                <Grid item xs={12} xl={12}>
                  <RadioGroupController
                    isRequired
                    name="bottleCap"
                    loaderColumns={6}
                    title={BOTTLE_CAP_TEXT}
                    options={BOTTLE_CAP_MAPPED}
                  />
                </Grid>

                <Grid item xs={12} xl={12}>
                  <SelectController
                    isRequired
                    name="bottleType"
                    title={BOTTLE_TYPE_TEXT}
                    options={BOTTLE_TYPE_MAPPED}
                  />
                </Grid>
              </>
            )}

            {/* Dynamic Forms For Formula Card  */}
            {formView()}

            <Grid item xs={12} xl={12}>
              <RadioGroupController
                name="coMfgCostType"
                loaderColumns={6}
                options={CO_MFG_COST_TYPE_MAPPED}
                title={
                  <Box sx={flexCenterJustifyBetween}>
                    <Box>
                      {CO_MFG_COST_TYPE_TEXT}
                      <ClearComponent onClick={handleCoMfgCostTypeClear} />
                    </Box>
                    <Typography color="primary">{OPTIONAL_TEXT}</Typography>
                  </Box>
                }
                handleOnChange={resetCoMfgCostPercentageValueOnChange}
              />
            </Grid>
            <Grid item xs={12} xl={12}>
              <InputController
                fieldType="number"
                name="coMfgCostPercentage"
                title={CO_MFG_COST_PERCENTAGE_TEXT}
              />
            </Grid>
          </Grid>
        </Card>
      </Grid>
    </Grid>
  );
};

export default Step1;
